import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useMembershipVisitDetailsAPI } from "@/app/hooks/api_hooks/Membership/useMembershipVisitDetailsAPI";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { IMembershipState } from "@/app_redux/reducers/slices/auth/membership.slice";
import { useEffect } from "react";

const MembershipUsageContent = ({ membershipVisitDetails }) => {
  const { veticDateFormat } = useUtils();
  const exhaustibleCoupons = membershipVisitDetails?.coupons_details?.exhaustible_coupons ?? [];

  return (
    <div className="bg-white rounded-[20px]">
      <p className="text-sm font-semibold text-gray-900">{membershipVisitDetails?.membership_name}</p>
      <div className="mt-3 rounded-2xl overflow-x-auto overflow-y-auto max-h-[65vh]">
        <table className="w-full min-w-[760px] md:min-w-[980px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
          <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal">
            <tr>
              <th
                scope="col"
                className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
              >
                Service
              </th>
              <th
                scope="col"
                className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
              >
                Total
              </th>
              <th
                scope="col"
                className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
              >
                Used
              </th>
              <th
                scope="col"
                className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
              >
                Visit Dates
              </th>
              <th
                scope="col"
                className="p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
              >
                Clinic
              </th>
            </tr>
          </thead>
          <tbody>
            {exhaustibleCoupons?.length > 0 ? (
              exhaustibleCoupons?.map((membership, id) => {
                const isFirst = id === 0;
                const isLast = id === exhaustibleCoupons.length - 1;
                const isSingle = exhaustibleCoupons.length === 1;
                const validBenefitHistory = membership?.benefit_history?.filter(
                  (history) => history?.visit_date || (history?.clinic_name && String(history.clinic_name).trim() !== ""),
                );
                const hasValidBenefitHistory = (validBenefitHistory?.length ?? 0) > 0;

                return (
                  <tr
                    key={id}
                    className={`
                      bg-white
                      ${isSingle ? "rounded-xl" : ""}
                      ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                      ${isLast && !isSingle ? "rounded-b-xl" : ""}
                    `}
                  >
                    <td
                      className={`
                        p-2 text-xs font-medium text-black
                        ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                        ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                        ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                      `}
                    >
                      {membership?.coupon_name || "NA"}
                    </td>
                    <td className="p-2 whitespace-nowrap text-xs text-black">
                      {membership?.usage_limit ?? "NA"}
                    </td>
                    <td className="p-2 whitespace-nowrap text-xs text-black">
                      {membership?.usage_count ?? "NA"}
                    </td>
                    <td className="p-2 align-top whitespace-nowrap text-xs text-black">
                      <div className="flex flex-col gap-1">
                        {hasValidBenefitHistory
                          ? validBenefitHistory?.map((history, index) => (
                              <span key={index}>{history?.visit_date ? veticDateFormat(history?.visit_date) : "NA"}</span>
                            ))
                          : "NA"}
                      </div>
                    </td>
                    <td
                      className={`
                        p-2 align-top text-xs text-black
                        ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                        ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                        ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                      `}
                    >
                      <div className="flex flex-col gap-1">
                        {hasValidBenefitHistory
                          ? validBenefitHistory?.map((history, index) => <span key={index}>{history?.clinic_name || "NA"}</span>)
                          : "NA"}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="bg-white">
                <td colSpan={5} className="p-4 text-center text-xs text-black rounded-xl">
                  No usage details available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
const MembershipUsageModal = () => {
  const { execute: fetchMembershipVisitData } = useMembershipVisitDetailsAPI();
  const modalState = useModalState("MEMBERSHIP_USAGE_MODAL");
  const { user_membership_id } = modalState || {};
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("MEMBERSHIP_SETUP", "id");
  const { routeState: membership } = useRouteInfo(routeId);
  const { membershipVisitDetails } = membership.state as IMembershipState;

  useEffect(() => {
    if (user_membership_id) {
      fetchMembershipVisitData(user_membership_id);
    }
  }, [user_membership_id]);

  return (
    <ModalPrompt
      modalId="MEMBERSHIP_USAGE_MODAL"
      showIcon={false}
      contentOverflow={true}
      titleIcon=""
      dialogClassName="w-full sm:my-8 sm:w-full sm:max-w-6xl p-4 sm:p-6"
      button={{
        primaryButtonText: "",
        secondaryButtonText: "",
        primaryButtonClassname: "bg-white",
      }}
      title="Membership Details"
      content={<MembershipUsageContent membershipVisitDetails={membershipVisitDetails} />}
    />
  );
};

export default MembershipUsageModal;
